/**
 * `<ts-wishlist-flyout>` — theme-owned added-to-wishlist confirmation.
 *
 * Rendered once per page (snippets/ts-wishlist-flyout.liquid, included from
 * layout/theme.liquid). Listens on `document` for `ts:swym:added` — dispatched
 * by ts-wishlist-button.js after a successful add — fills in the product
 * thumbnail, and shows itself for 6 seconds (per UX: 5–7s) or until the close
 * button is clicked. The countdown suspends while the card is hovered or holds
 * focus (WCAG 2.2.1) and restarts once the user lets go.
 *
 * Shown as a `popover="manual"` element so it enters the top layer and paints
 * above open Quick View / Shop the Look dialogs (while relocating inside the
 * open dialog so the modal's inertness doesn't swallow its clicks — see
 * #relocate); `[data-open]` carries the visible state so the flyout still
 * works if the Popover API is unavailable (it just stacks via z-index then).
 */

import { SWYM_ADDED_EVENT, SWYM_ERROR_EVENT } from '@ts/utility-swym';

const DISMISS_AFTER_MS = 6000;

class TSWishlistFlyout extends HTMLElement {
  /** @type {number | null} Auto-dismiss timer handle. */
  #dismissTimer = null;

  connectedCallback() {
    document.addEventListener(SWYM_ADDED_EVENT, this.#onAdded);
    document.addEventListener(SWYM_ERROR_EVENT, this.#onError);
    this.querySelector('.ts-wishlist-flyout__close')?.addEventListener('click', this.#onClose);
    this.querySelector('.ts-wishlist-flyout__image')?.addEventListener('error', this.#onImageError);
    this.addEventListener('mouseenter', this.#pauseDismiss);
    this.addEventListener('focusin', this.#pauseDismiss);
    this.addEventListener('mouseleave', this.#resumeDismiss);
    this.addEventListener('focusout', this.#resumeDismiss);
  }

  disconnectedCallback() {
    document.removeEventListener(SWYM_ADDED_EVENT, this.#onAdded);
    document.removeEventListener(SWYM_ERROR_EVENT, this.#onError);
    this.querySelector('.ts-wishlist-flyout__close')?.removeEventListener('click', this.#onClose);
    this.querySelector('.ts-wishlist-flyout__image')?.removeEventListener('error', this.#onImageError);
    this.removeEventListener('mouseenter', this.#pauseDismiss);
    this.removeEventListener('focusin', this.#pauseDismiss);
    this.removeEventListener('mouseleave', this.#resumeDismiss);
    this.removeEventListener('focusout', this.#resumeDismiss);
    this.#hide();
  }

  /** @param {Event} event */
  #onAdded = (event) => {
    const product = /** @type {CustomEvent} */ (event).detail?.product;
    this.#show(product);
  };

  /** A Swym add/remove failed — same card, error message instead. @param {Event} event */
  #onError = (event) => {
    const product = /** @type {CustomEvent} */ (event).detail?.product;
    this.#show(product, true);
  };

  #onClose = () => {
    this.#hide();
  };

  /** Suspend auto-dismiss while the pointer or keyboard focus holds the card (WCAG 2.2.1). */
  #pauseDismiss = () => {
    if (this.#dismissTimer == null) return;
    clearTimeout(this.#dismissTimer);
    this.#dismissTimer = null;
  };

  /** Restart auto-dismiss once neither the pointer nor focus holds the card. @param {Event} event */
  #resumeDismiss = (event) => {
    if (!this.hasAttribute('data-open')) return;
    // focusout also fires for moves within the card — only resume when focus left it.
    const next = /** @type {FocusEvent} */ (event).relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    // The other modality may still hold the card.
    if (event.type === 'mouseleave' && this.matches(':focus-within')) return;
    if (event.type === 'focusout' && this.matches(':hover')) return;
    this.#armDismiss();
  };

  /** (Re)start the auto-dismiss countdown. */
  #armDismiss() {
    if (this.#dismissTimer != null) clearTimeout(this.#dismissTimer);
    this.#dismissTimer = setTimeout(() => this.#hide(), DISMISS_AFTER_MS);
  }

  /** A thumbnail URL that fails to load must not show a broken-image glyph. @param {Event} event */
  #onImageError = (event) => {
    if (!(event.target instanceof HTMLImageElement)) return;
    console.warn('[ts-wishlist-flyout] thumbnail failed to load:', event.target.currentSrc || event.target.src);
    this.#showPlaceholder(event.target);
  };

  /**
   * Swap in the placeholder asset (data-placeholder, same SVG as the product
   * card's no-image state). Hiding is the last resort — only when there is no
   * placeholder configured or the placeholder itself failed to load.
   * @param {HTMLImageElement} image
   */
  #showPlaceholder(image) {
    const placeholder = this.dataset.placeholder;
    if (!placeholder || image.getAttribute('src') === placeholder) {
      image.hidden = true;
      return;
    }
    image.hidden = false;
    image.src = placeholder;
    image.alt = '';
  }

  /**
   * A modal <dialog> makes everything outside its subtree inert — top-layer
   * popovers included — so the close button wouldn't register clicks. Host
   * the flyout inside the open dialog while it shows (top-layer layout is
   * viewport-relative either way) and send it home when the dialog closes.
   * No-op on a plain page. Relocation fires disconnected/connectedCallback,
   * which is safe: the listeners re-attach and #hide is idempotent.
   */
  #relocate() {
    /** @type {Element | null} */
    let host = null;
    try {
      host = document.querySelector('dialog:modal');
    } catch {
      host = document.querySelector('dialog[open]');
    }
    if (!(host instanceof HTMLElement) || host.contains(this)) return;
    host.append(this);
    host.addEventListener('close', this.#onHostClose, { once: true });
  }

  /** @param {Event} event */
  #onHostClose = (event) => {
    // Ignore a stale listener from a previous host the flyout has since left.
    if (!(event.target instanceof HTMLElement) || !event.target.contains(this)) return;
    this.#hide();
    document.body.append(this);
  };

  /**
   * Fill in the thumbnail and show the card; repeat adds restart the timer.
   * @param {{ iu?: string, dt?: string } | undefined} product
   * @param {boolean} [error] - Show the error message (data-error styles) instead.
   */
  #show(product, error = false) {
    this.#relocate();

    this.toggleAttribute('data-error', error);
    const message = this.querySelector('.ts-wishlist-flyout__message');
    const text = error ? this.dataset.errorMessage : this.dataset.addedMessage;
    if (message && text) message.textContent = text;

    const image = this.querySelector('.ts-wishlist-flyout__image');
    if (image instanceof HTMLImageElement) {
      if (product?.iu) {
        image.hidden = false;
        image.src = product.iu;
        image.alt = product.dt ?? '';
      } else {
        // No thumbnail on the add event — show the placeholder, never a
        // stale image from a previous add.
        this.#showPlaceholder(image);
      }
    }

    this.setAttribute('data-open', '');
    try {
      if (!this.matches(':popover-open')) this.showPopover();
    } catch {
      // Popover API unavailable — [data-open] alone shows the card.
    }

    // A repeat add while the user is holding the card must not start a
    // countdown under them — #resumeDismiss arms it when they let go.
    if (!this.matches(':hover') && !this.matches(':focus-within')) this.#armDismiss();
  }

  #hide() {
    if (this.#dismissTimer != null) {
      clearTimeout(this.#dismissTimer);
      this.#dismissTimer = null;
    }
    // Clear the thumbnail so the next show can't flash the previous product
    // while its own image is still loading.
    const image = this.querySelector('.ts-wishlist-flyout__image');
    if (image instanceof HTMLImageElement) {
      image.hidden = true;
      image.removeAttribute('src');
      image.alt = '';
    }
    this.removeAttribute('data-open');
    try {
      if (this.matches(':popover-open')) this.hidePopover();
    } catch {
      // Popover API unavailable.
    }
  }
}

if (!customElements.get('ts-wishlist-flyout')) {
  customElements.define('ts-wishlist-flyout', TSWishlistFlyout);
}
